import type { GroupEncryptionStatus } from '@/configs/apis/groups/interface';
import type { ChatMessagesArgType } from '../useChatMessages/types';
import type { useChatGroup } from '.';

export type UseChatGroupResult = ReturnType<typeof useChatGroup>;
// Group
export interface ChatGroup {
  id: number;
  category: number;
  group_id: string;
  creator_uid: string;
  parent_group_id: string;
  name: string;
  avatar: string;
  status: number;
  create_at: number;
  update_at: number;
  is_encryption: GroupEncryptionStatus;
}

export type ChatGroupQuery = Pick<ChatGroup, 'group_id'>;

export type ChatGroupGroupId = ChatGroup['group_id'];

export type ChatGroupParams = ChatGroupQuery & Partial<ChatGroup>;

export type ChatGroupMap = Record<ChatGroupGroupId, ChatGroup>;

export type ChatGroupArgsType<T> = {
  id: ChatGroupGroupId;
  is_encryption?: GroupEncryptionStatus;
  args: T[];
};

export type ChatGroupIdsArgs = ChatGroupArgsType<ChatGroupGroupId>;

// Settings
export type ChatGroupSettings = {
  group_name: string;
  description: string;
  share_url?: string;
  speak_person?: string[];
  speak_limit?: { enable: number; time: number; count: number };
  is_encryption: GroupEncryptionStatus;
};

export type ChatGroupSettingsParams = ChatGroupQuery & Partial<ChatGroupSettings>;

export type ChatGroupSettingsMap = Record<ChatGroupGroupId, ChatGroupSettings>;

export type ChatGroupSettingsPropArgs = ChatGroupArgsType<string>;

export enum ChatGroupMemberRemoved {
  Normal = 0,
  Removed = 1,
}

// Member
// TODO amo: use 'User' interface?
export interface ChatGroupMember {
  id: number;
  category: number;
  user_id: string;
  name: string;
  username?: string;
  avatar: string;
  remark_name: string;
  role: number;
  create_at: number;
  update_at: number;
  removed: ChatGroupMemberRemoved;
}

export type ChatGroupMemberQuery = Pick<ChatGroupMember, 'user_id'>;

export type ChatGroupMemberId = ChatGroupMember['user_id'];

export type ChatGroupMemberArgs = ChatGroupArgsType<ChatGroupMember>;

export type ChatGroupHalfMemberArgs = ChatGroupArgsType<
  ChatGroupMember | ChatGroupMemberId
>;

export type ChatGroupMemberParams = ChatGroupMemberQuery & Partial<ChatGroupMember>;

export type ChatGroupMemberParamsArgs = ChatGroupArgsType<ChatGroupMemberParams>;

export type ChatGroupMemberIdArgs = ChatGroupArgsType<ChatGroupMemberId>;

export interface ChatGroupMemberInfo {
  index: number;
  member: ChatGroupMember;
}

export type ChatGroupMemberInfoMap = Record<ChatGroupMemberId, ChatGroupMemberInfo>;

export interface ChatGroupMembersInfo {
  members: ChatGroupMember[];
  normalMembers: ChatGroupMember[];
  membersInfoMap: ChatGroupMemberInfoMap;
}

export type ChatGroupMembersMap = Record<ChatGroupGroupId, ChatGroupMembersInfo>;
// export type ChatGroupMembersMap = Record<ChatGroupGroupId, ChatGroupMember[]>;

// Read states
export interface DBChatGroupReadState {
  id: number;
  message_id: string;
  reader_ids: string;
  expect_read_list: string;
  create_at: number;
  expire_at: number;
}

export type ChatGroupReadState = DBChatGroupReadState;

export type ChatGroupReadStatePartial = Partial<DBChatGroupReadState>;

export type ChatGroupReadStateQuery = Pick<DBChatGroupReadState, 'message_id'>;

export type ChatGroupReadStateId = DBChatGroupReadState['message_id'];

export type ChatGroupReadStateParams = ChatGroupReadStateQuery &
  Partial<DBChatGroupReadState>;

export type ChatGroupReadStateMap = Record<ChatGroupGroupId, ChatGroupReadState>;

export type ChatGroupReadStateReaderArgParams = ChatMessagesArgType<
  ChatGroupMember['user_id']
>;

// Mixin
export interface ChatGroupInfo extends ChatGroup {
  settings: ChatGroupSettings;
  members: ChatGroupMember[];
}
